import { Flex, Grid, Typography, theme } from 'antd';
import type { ModuleKey } from '@titularis/ui';
import type { StudyStatus } from '@titularis/api-client';
import { produto as arteDoProduto } from '../produtos';
import { contar, dataCurta } from '../texto';
import { CabecalhoDaTela } from './CabecalhoDaTela';

/**
 * O cabeçalho da tela de estudo: quem é o módulo, e em que pé você está nele.
 *
 * **O que havia era um título e dois chips.** A palavra "Enac" sozinha, e
 * embaixo dela duas etiquetas cinzas iguais - "Restam 3 respostas hoje",
 * "Resta 1 resolução hoje" - com um link solto ao lado. Três objetos do mesmo
 * peso visual, nenhum com hierarquia, ocupando a faixa que devia responder
 * "onde eu estou e o que eu posso fazer aqui".
 *
 * O desenho agora tem **dois lados**, e cada um responde uma pergunta:
 *
 * - à esquerda a **identidade** - o nome do módulo e a linha que diz o que ele
 *   é;
 * - à direita o **estado** - e é aqui que a conta paga e a gratuita deixam de
 *   ser a mesma tela.
 *
 * **A diferença entre quem pagou e quem não pagou é o assunto do lado
 * direito**, não a ausência de um aviso:
 *
 * | | gratuita | liberada |
 * | --- | --- | --- |
 * | estado | a cota da semana, com medidor | "Sem limite semanal" e o prazo do acesso |
 * | ação | "Ver os planos" | nenhuma - não há o que vender a quem comprou |
 * | fio | a cor do módulo | o degradê inteiro |
 *
 * **Houve um selo no título**, no vocabulário do rodapé da barra ("Grátis" e
 * "Pro"), primeiro pendurado no pé da palavra e depois no canto de cima. Saiu
 * em 08/09/2026: no pé ele comia as letras, e em cima - onde "Enac" não tem
 * tinta - passou a pairar sobre o vazio, lendo como marcador sobrescrito e não
 * como carimbo. O plano já está dito no lado direito e no rodapé da barra; a
 * terceira vez era enfeite.
 */
export function CabecalhoDoModulo({
  module,
  titulo,
  status,
  aoVerPlanos,
}: {
  module: ModuleKey;
  titulo: string;
  /** `undefined` enquanto a resposta não chega: aí o lado direito fica vazio. */
  status?: StudyStatus;
  aoVerPlanos: () => void;
}) {
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();
  const arte = arteDoProduto(module);
  const acento = arte?.acento ?? token.colorPrimary;

  /*
   * No celular a fileira quebra e a coluna do estado passa a ser a segunda
   * linha do cabeçalho: alinhada à direita, ela ficaria com o rótulo miúdo
   * recuado no meio da tela, sem nada com que se alinhar. Quebrada, ela começa
   * onde tudo começa - é a mesma regra da `LinhaDeAjuste`.
   */
  const alinhamento = telas.md ? 'flex-end' : 'flex-start';

  const liberado = status ? status.modulosLiberados.includes(module) : undefined;
  const acesso = status?.acessos.find((item) => item.product === module && item.ativo);

  return (
    <CabecalhoDaTela
      titulo={titulo}
      resumo={arte?.resumo}
      acento={acento}
      premium={liberado}
      direita={
        status && liberado ? (
          <AcessoLiberado ate={acesso?.expiresAt ?? null} alinhamento={alinhamento} />
        ) : status ? (
          <CotaDaSemana
            status={status}
            acento={acento}
            alinhamento={alinhamento}
            aoVerPlanos={aoVerPlanos}
          />
        ) : null
      }
    />
  );
}

/** O rótulo miúdo que abre as duas colunas de estado. */
function Etiqueta({ children }: { children: string }) {
  const { token } = theme.useToken();

  return (
    <Typography.Text
      type="secondary"
      style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}
    >
      <span style={{ color: token.colorTextTertiary }}>{children}</span>
    </Typography.Text>
  );
}

/**
 * O que a conta paga vê no lugar da cota: o que ela comprou, e até quando.
 *
 * **Não é a cota escondida - é outra informação.** Quem pagou não tem número
 * para acompanhar; tem um prazo, e é a única coisa sobre o plano que ainda lhe
 * interessa nesta tela.
 */
function AcessoLiberado({
  ate,
  alinhamento,
}: {
  ate: string | null;
  alinhamento: 'flex-start' | 'flex-end';
}) {
  const { token } = theme.useToken();

  return (
    <Flex vertical align={alinhamento} gap={2}>
      <Etiqueta>Seu acesso</Etiqueta>
      <Typography.Text strong>Sem limite semanal</Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {ate ? `Vale até ${dataCurta(ate)}` : 'Sem prazo'}
      </Typography.Text>
    </Flex>
  );
}

/**
 * A cota da semana com medidor, no lugar dos dois chips.
 *
 * **O medidor existe porque o número sozinho não tem escala.** "Restam 3
 * questões" não diz se três é muito ou pouco; "3 de 5" diz, e a barra diz sem
 * ler. Os totais vêm do servidor (`limites`), então, quando a régua mudar,
 * a tela acompanha.
 */
function CotaDaSemana({
  status,
  acento,
  alinhamento,
  aoVerPlanos,
}: {
  status: StudyStatus;
  acento: string;
  alinhamento: 'flex-start' | 'flex-end';
  aoVerPlanos: () => void;
}) {
  const { token } = theme.useToken();

  return (
    <Flex vertical align={alinhamento} gap={6} style={{ width: 208 }}>
      <Etiqueta>Cota da semana</Etiqueta>

      <Medidor
        restante={status.restante.answers}
        total={status.limites.freeWeeklyAnswers}
        singular="questão"
        plural="questões"
        acento={acento}
      />
      <Medidor
        restante={status.restante.commentaries}
        total={status.limites.freeWeeklyCommentaries}
        singular="resolução"
        plural="resoluções"
        acento={acento}
      />

      <Typography.Link onClick={aoVerPlanos} style={{ fontSize: token.fontSizeSM }}>
        Ver os planos
      </Typography.Link>
    </Flex>
  );
}

function Medidor({
  restante,
  total,
  singular,
  plural,
  acento,
}: {
  restante: number;
  total: number;
  singular: string;
  plural: string;
  acento: string;
}) {
  const { token } = theme.useToken();
  const acabou = restante === 0;
  const cheio = total > 0 ? Math.min(100, Math.round((restante / total) * 100)) : 0;

  return (
    <Flex vertical gap={3} style={{ width: '100%' }}>
      {/*
        "3 de 3 questões", e não o número numa ponta e o total na outra: numa
        coluna de 200px, o par afastado lia como duas informações soltas em vez
        de uma fração. O que fica forte é o que resta - é o número que muda.
      */}
      <Typography.Text style={{ fontSize: token.fontSizeSM }}>
        <strong style={{ color: acabou ? token.colorError : undefined }}>{restante}</strong> de{' '}
        {contar(total, singular, plural)}
      </Typography.Text>

      <div
        style={{
          height: 4,
          borderRadius: 2,
          background: token.colorFillSecondary,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${cheio}%`,
            height: '100%',
            borderRadius: 2,
            background: acento,
            transition: 'width 0.3s ease-out',
          }}
        />
      </div>
    </Flex>
  );
}
